import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { resolveBrand, type ResolvedBrand, type Tenant } from '@kawai/shared';
import { fetchTenantById } from './repositories/tenants.js';

// 純粋な解決ロジック（resolveBrand / ResolvedBrand）は `@kawai/shared` に集約済（S-10）。
// 本ファイルには React / Firebase 依存の hook と、このアプリ固有のデフォルトだけを残す。
export type { ResolvedBrand };

/**
 * S-04: tenant-portal のデフォルトブランド。
 * tenant doc 取得前 / 取得失敗 / saas-admin（tenantId=null）時に表示する。
 */
export const APP_DEFAULT_BRAND: ResolvedBrand = {
  brandName: 'セルフ武生21SS',
  primaryColor: '#0F172A',
};

export type TenantBrandStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseTenantBrandResult {
  status: TenantBrandStatus;
  brand: ResolvedBrand;
  /**
   * S-90: fetch した `tenants/{tid}` doc そのもの。ホームの契約情報カード
   * （status / features / contractStartedAt / billingStatus / payjpDefaultCard）で
   * 使う。brand と同じ 1 回の fetch から取れるため追加 I/O ゼロ。
   * 取得前 / 失敗 / doc 不在 / saas-admin（tenantId=null）は null。
   */
  tenant: Tenant | null;
}

/**
 * S-90 (PR #467 /review): Layout が 1 回だけ `useTenantBrand` を実行し、その結果を
 * 配下ページ（HomePage 等）へ共有するための context。Layout と各ページが hook を
 * 独立に呼ぶと同一 `tenants/{tid}` doc の read が二重化するため（レビュー指摘）、
 * ページ側は `useTenantBrandContext()` で受け取る。
 */
export const TenantBrandContext = createContext<UseTenantBrandResult | null>(null);

/** Layout（provider）配下でのみ使用可。外で呼んだら配線ミスなので即例外にする。 */
export function useTenantBrandContext(): UseTenantBrandResult {
  const ctx = useContext(TenantBrandContext);
  if (!ctx) {
    throw new Error(
      'useTenantBrandContext は Layout（TenantBrandContext.Provider）配下でのみ使用できます',
    );
  }
  return ctx;
}

/**
 * S-04: claims.tenantId に紐づく `tenants/{tid}` doc を取得し、
 * `customizations` を解決してブランド表示値を返す hook。
 *
 * - `tenantId === null`（saas-admin）→ APP_DEFAULT_BRAND を即返す（fetch しない）
 * - 取得失敗時もアプリは止めず APP_DEFAULT_BRAND にフォールバック（status='error'）
 * - test 時は `fetcher` を差し替え可能
 *
 * fetch は `tenantId` が変わったときだけ走る（= アプリ起動時 1 回 / S-10 要件）。
 */
export function useTenantBrand(
  tenantId: string | null,
  fetcher: (id: string) => Promise<Tenant | null> = fetchTenantById,
): UseTenantBrandResult {
  // fetcher を初回値に固定する。利用側が無名 arrow を渡しても useEffect の deps に
  // fetcher を含めないことで、毎レンダー再 fetch（無限ループ）になるのを防ぐ
  // （S-04 PR #88 指摘 / コピー時の事故防止）。
  const fetcherRef = useRef(fetcher);

  const [state, setState] = useState<UseTenantBrandResult>({
    status: tenantId ? 'loading' : 'idle',
    brand: APP_DEFAULT_BRAND,
    tenant: null,
  });

  useEffect(() => {
    if (!tenantId) {
      setState({ status: 'idle', brand: APP_DEFAULT_BRAND, tenant: null });
      return;
    }
    let cancelled = false;
    setState((prev) => ({
      status: 'loading',
      brand: prev.brand,
      tenant: prev.tenant,
    }));
    fetcherRef
      .current(tenantId)
      .then((tenant) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          brand: resolveBrand(tenant, APP_DEFAULT_BRAND),
          tenant,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 致命ではないので APP_DEFAULT_BRAND にフォールバック
        // eslint-disable-next-line no-console
        console.error('[tenantBrand] fetch failed:', err);
        setState({ status: 'error', brand: APP_DEFAULT_BRAND, tenant: null });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return state;
}
